// src/modules/app-views/account/pages/Subscription/tabs/RecordTab/OperatorsCard.jsx
// *******************************************************************************
// OPERATOR SELECTION CARD - Inline grid for activating/deactivating operators
// Controlled component: parent manages activeIds via value/onChange
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, theme, Tooltip, Grid, Avatar, Tag, Spin, Badge } from 'antd';
import { CheckCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { TfiWorld } from 'react-icons/tfi';
import { TbPlugConnectedX, TbPlugConnected } from 'react-icons/tb';
import { PiPlugsConnectedBold } from 'react-icons/pi';
import { MdOutlineLuggage } from 'react-icons/md';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OperatorsCard = ({ value = [], onChange, onLoad, disabled = false, scrollLimit = true }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();
  // Stable ref for onLoad to avoid infinite fetch loop
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchOperators = useCallback(async () => {
    setLoading(true);
    try {
      const [data] = await axiosService.get({
        url: API.SYS_OPERATORS_API.LIST,
      });
      const list = data || [];
      setOperators(list);

      // Report replicated IDs to parent as initial state
      const replicatedIds = list.filter(op => op.__isReplicated).map(op => op._id);
      onLoadRef.current?.(replicatedIds);
    } catch {
      appService.message({ flag: 'error', text: translateX('error_loading_data') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleToggle = id => {
    if (disabled) return;

    const newValue = value.includes(id) ? value.filter(x => x !== id) : [...value, id];

    onChange?.(newValue);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // On LG screens and above, limit to 2 rows with scrollbar (when scrollLimit is enabled)
  const scrollContainerStyle = scrollLimit && screens.lg
    ? { maxHeight: 256, overflowY: 'auto', overflowX: 'hidden' }
    : {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // const cardTitle = <div className="text-muted">{translateX('operators')}</div>;

  const selectedCount = value.length;

  const cardTitle = (
    <div className="text-muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>
        {translateX('operators')}
        <Tooltip title={translateX('operators_info_description')}>
          <QuestionCircleOutlined style={{ marginLeft: 8, cursor: 'normal' }} />
        </Tooltip>
      </span>
      {selectedCount > 0 && (
        <Badge count={selectedCount} style={{ backgroundColor: token.colorPrimary }} />
      )}
    </div>
  );

  return (
    <Card id="operators-card" title={cardTitle} className="no-shadow">
      <Spin spinning={loading}>
        <div style={scrollContainerStyle}>
          <Row gutter={[12, 12]}>
            {operators.map(op => {
              const isActive = value.includes(op._id);
              const apiTag = buildApiTag(op);
              const tooltipText = buildTooltipText(op);

              return (
                <Col xs={24} sm={12} md={8} lg={6} key={op._id}>
                  <div
                    onClick={() => handleToggle(op._id)}
                    style={buildCardStyle(isActive, token, disabled)}
                  >
                    {/* check icon */}
                    {isActive && (
                      <CheckCircleFilled
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: disabled ? token.colorTextDisabled : token.colorPrimary,
                          fontSize: 16,
                        }}
                      />
                    )}

                    {/* avatar */}
                    {op.logoUrl ? (
                      <Avatar
                        src={op.logoUrl}
                        size={36}
                        style={{
                          marginBottom: 4,
                          border: `1px solid ${token.colorBorderSecondary}`,
                        }}
                      />
                    ) : (
                      <Avatar
                        icon={<TfiWorld />}
                        size={36}
                        style={{
                          backgroundColor: op.backColor || '#1890ff',
                          marginBottom: 4,
                          border: `1px solid ${token.colorBorderSecondary}`,
                        }}
                      />
                    )}

                    {/* name */}
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: isActive && !disabled ? 600 : 400,
                        color: disabled
                          ? token.colorTextDisabled
                          : isActive
                            ? token.colorText
                            : token.colorTextSecondary,
                        textAlign: 'center',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {op.name}
                    </div>

                    {/* API tag + plans icon */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 4,
                      }}
                    >
                      <Tooltip title={apiTag.tooltip}>
                        <Tag
                          color={apiTag.color}
                          icon={apiTag.icon}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            lineHeight: '16px',
                            padding: '0 4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          {apiTag.label}
                        </Tag>
                      </Tooltip>

                      <Tooltip title={tooltipText}>
                        <MdOutlineLuggage
                          style={{
                            fontSize: 16,
                            color: token.colorTextSecondary,
                            cursor: 'normal',
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </Spin>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OperatorsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildApiTag(op) {
  // A) No API available in this environment → red
  // (either no API developed or not released for propagation yet)
  if (!op.__apiAvailable) {
    return {
      color: 'red',
      icon: <TbPlugConnectedX style={{ fontSize: 12 }} />,
      label: translateX('no_api'),
      tooltip: translateX('api_not_available'),
    };
  }

  // C) API available and enabled by tenant → blue
  if (op.__apiEnabled) {
    return {
      color: 'blue',
      icon: <PiPlugsConnectedBold style={{ fontSize: 12 }} />,
      label: 'API',
      tooltip: translateX('api_connected'),
    };
  }

  // B) API available but not enabled by tenant → yellow/gold
  return {
    color: 'gold',
    icon: <TbPlugConnected style={{ fontSize: 12 }} />,
    label: 'API',
    tooltip: translateX('api_disconnected'),
  };
}

function buildTooltipText(op) {
  const total = op.plansCount || 0;
  const available = op.__tenantPlansCount || 0;

  if (op.__isReplicated && available > 0) {
    return translateX('operator_plans_with_available', { total, available });
  }

  return translateX('operator_plans_total', { total });
}

function buildCardStyle(isSelected, token, disabled) {
  const borderColor = disabled
    ? token.colorBorderSecondary
    : isSelected
      ? token.colorPrimary
      : token.colorBorderSecondary;

  const bgColor = disabled
    ? token.colorFillTertiary
    : isSelected
      ? token.colorPrimaryBg
      : token.colorBgContainer;

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 8px',
    height: 116,
    borderRadius: token.borderRadius,
    border: `1px solid ${borderColor}`,
    backgroundColor: bgColor,
    boxShadow: isSelected && !disabled ? `0 2px 8px ${token.colorPrimaryBgHover}` : 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none',
  };
}
